/**
 * Phase 7.5 hotfix · `.quiz-myth` 3-state gated reveal handler.
 * Ported from `01_BUILD/qa_studio.html` L22712 `initQuizMyths()`.
 *
 * Mechanic: each myth-card is a junior-belief statement that's always FALSE
 * (a myth). User picks `agree` (incorrect) or `disagree` (correct) · click
 * reveals truth panel + verdict text + colour-codes the picked button.
 *
 * Markup contract (per qa_studio.html S01 M1-M7):
 *   <div class='quiz-myths-grid'>
 *     <div class='quiz-myth' data-myth='<n>'>
 *       <div class='qm-header'>...</div>
 *       <div class='qm-statement'>...statement text...</div>
 *       <div class='qm-options'>
 *         <button class='qm-btn' data-answer='agree'>👍 Согласен</button>
 *         <button class='qm-btn' data-answer='disagree'>👎 Не согласен</button>
 *       </div>
 *       <div class='qm-reveal'>
 *         <div class='qm-verdict'></div>  <!-- filled on answer -->
 *         <div class='qm-truth-tag'>★ ПРАВДА</div>
 *         <div class='qm-truth'>...truth body...</div>
 *       </div>
 *     </div>
 *     ...
 *   </div>
 *
 * Gating: `.qm-reveal { max-height: 0 }` by default. CSS rule
 * `.quiz-myth.answered .qm-reveal { max-height: 800px }` unlocks on answer.
 *
 * State persistence: session-only (per pedagogy · re-attempt on revisit).
 */
export function initMythCards(root: HTMLElement): () => void {
  const cards = Array.from(root.querySelectorAll<HTMLElement>('.quiz-myth'));
  const cleanups: Array<() => void> = [];

  for (const card of cards) {
    if (card.dataset.mythInit === '1') continue;
    card.dataset.mythInit = '1';
    const buttons = Array.from(card.querySelectorAll<HTMLButtonElement>('.qm-btn'));
    // wave2 (MS-03): data-truth='true' = утверждение-ПРАВДА (correct = agree).
    // Default (атрибута нет / 'false') = миф (correct = disagree) — обратная
    // совместимость со всеми существующими тикетами.
    const isTruth = card.getAttribute('data-truth') === 'true';

    const onClick = (e: MouseEvent) => {
      const btn = e.currentTarget as HTMLButtonElement;
      if (card.classList.contains('answered')) return;
      const answer = btn.getAttribute('data-answer');
      const isCorrect = isTruth ? answer === 'agree' : answer === 'disagree';
      const verdictClass = isCorrect ? 'correct' : 'wrong';
      card.classList.add('answered', verdictClass);
      btn.classList.add('selected', verdictClass);
      buttons.forEach((b) => {
        b.disabled = true;
      });
      const verdict = card.querySelector<HTMLElement>('.qm-verdict');
      if (verdict) {
        verdict.textContent = isTruth
          ? (isCorrect ? '✓ Правильно — это правда.' : '✗ А вот это как раз правда. Смотри ↓')
          : (isCorrect ? '✓ Правильно — это миф.' : '✗ На самом деле это миф. Смотри почему ↓');
      }
    };

    buttons.forEach((btn) => btn.addEventListener('click', onClick));
    cleanups.push(() => {
      buttons.forEach((btn) => btn.removeEventListener('click', onClick));
      delete card.dataset.mythInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
